import { useState } from 'react';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  useToast,
} from '@adop/ui';
import { Plus, ShieldCheck, Trash2, Users } from 'lucide-react';
import { addPlatformAdmin, fetchAdminAdmins, removePlatformAdmin } from '../../lib/admin-api';
import { errorMessage } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { formatDate } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';

export function AdminAdminsPage() {
  const { toast } = useToast();
  const state = useAsync(fetchAdminAdmins, []);

  const [addOpen, setAddOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ user_id: string; label: string } | null>(null);

  const admins = state.data?.admins ?? [];
  const lastAdmin = admins.length <= 1;

  const openAdd = () => {
    setEmail('');
    setAddError(null);
    setAddOpen(true);
  };

  const submitAdd = async () => {
    setBusy(true);
    setAddError(null);
    try {
      const result = await addPlatformAdmin({ email });
      toast({ title: `Added ${result.email}`, variant: 'success' });
      setAddOpen(false);
      state.reload();
    } catch (cause) {
      setAddError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      await removePlatformAdmin(removeTarget.user_id);
      toast({ title: 'Platform admin removed', variant: 'success' });
      state.reload();
    } catch (cause) {
      toast({ title: 'Could not remove admin', description: errorMessage(cause), variant: 'error' });
    } finally {
      setRemoveTarget(null);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Admins"
        description="Accounts that can access this console. The API will never remove the last one."
        action={
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" /> Add admin
          </Button>
        }
      />

      {state.error ? <Alert>{state.error}</Alert> : null}

      <Card>
        {state.loading && !state.data ? (
          <LoadingBlock />
        ) : admins.length === 0 ? (
          <EmptyState icon={<Users className="h-6 w-6" />} title="No platform admins" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Added</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {admins.map((admin) => (
                  <tr key={admin.user_id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 font-medium text-zinc-900">
                        <ShieldCheck className="h-4 w-4 text-zinc-400" />
                        {admin.email ?? admin.user_id}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{admin.full_name ?? '—'}</td>
                    <td className="px-4 py-3 text-zinc-600">{formatDate(admin.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={lastAdmin}
                        title={lastAdmin ? 'You cannot remove the last platform admin.' : undefined}
                        onClick={() =>
                          setRemoveTarget({
                            user_id: admin.user_id,
                            label: admin.email ?? admin.user_id,
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {lastAdmin && admins.length > 0 ? (
        <Alert variant="info">
          The last platform admin cannot be removed. Add another admin before removing this one.
        </Alert>
      ) : null}

      <Modal
        open={addOpen}
        onClose={busy ? () => undefined : () => setAddOpen(false)}
        closeOnBackdrop={!busy}
        closeOnEscape={!busy}
        title="Add platform admin"
        description="The account must already exist in auth.users."
        footer={
          <>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button loading={busy} disabled={email.trim().length === 0} onClick={() => void submitAdd()}>
              Add admin
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {addError ? <Alert>{addError}</Alert> : null}
          <Input
            label="Email"
            type="email"
            value={email}
            autoComplete="off"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="person@example.com"
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove platform admin?"
        message={
          removeTarget
            ? `${removeTarget.label} will immediately lose access to the admin console.`
            : undefined
        }
        confirmLabel="Remove"
        variant="danger"
        onCancel={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
      />
    </div>
  );
}
